import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { LogOut, UserRound } from "lucide-react";
import { meQuery } from "@/auth/queries";
import { useLogout } from "@/auth/useLogout";
import { fr } from "@/i18n/fr";

interface AccountMenuProps {
	/** Over the ink hero: light text and borders. */
	onInk?: boolean;
	/** Icon buttons only (the viewer header). */
	compact?: boolean;
}

/** "Se connecter" without a session; the account and "Se déconnecter" with one. */
export function AccountMenu({ onInk, compact }: AccountMenuProps) {
	const me = useQuery(meQuery());
	const logout = useLogout();
	// come back to this page after signing in (the landing leads to the catalogue)
	const here = useRouterState({ select: (s) => s.location.href });
	const pill = `inline-flex h-9 items-center gap-2 rounded-pill px-2 text-sm no-underline transition ${onInk ? "border border-on-ink/25 text-on-ink hover:bg-on-ink/10 hover:text-on-ink" : "border border-line-strong bg-bg-elev text-fg hover:bg-chip hover:text-fg"}`;

	if (me.isPending) return null;
	if (!me.data)
		return (
			<Link
				to="/connexion"
				search={{ redirect: here === "/" ? undefined : here }}
				className={`${pill} sm:px-4`}
				aria-label={fr.signIn}
			>
				<UserRound className="size-5 sm:hidden" aria-hidden="true" />
				<span className="hidden sm:inline">{fr.signIn}</span>
			</Link>
		);
	const who = me.data.name ?? me.data.email;
	return (
		<span className="inline-flex items-center gap-2">
			{!compact && (
				<span
					className={`hidden max-w-48 truncate text-sm md:inline ${onInk ? "text-on-ink/70" : "text-fg-muted"}`}
					title={me.data.email}
				>
					{who}
				</span>
			)}
			<button
				type="button"
				className={`${pill} ${compact ? "" : "sm:px-4"}`}
				onClick={() => logout.mutate()}
				disabled={logout.isPending}
				aria-label={fr.signOut}
				title={`${fr.signOut} (${me.data.email})`}
			>
				<LogOut
					className={`size-5 ${compact ? "" : "sm:hidden"}`}
					aria-hidden="true"
				/>
				{!compact && <span className="hidden sm:inline">{fr.signOut}</span>}
			</button>
		</span>
	);
}
